import { isAxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';

import type { ProductAPI } from '~/api/Catalog';
import { getRequestLang } from '~/api/utils';
import { company } from '~/company/Company';
import { orderStore } from '~/stores';
import { bottomNavigationStore } from '~/stores/BottomNavigationStore';
import type { Offer } from '~/stores/CategoriesStore/Offer';
import {
  getProductById,
  type LoadProductParams,
  Product,
} from '~/stores/CategoriesStore/Product';
import { ReviewsStore } from '~/stores/ReviewsStore';
import { Task } from '~/stores/shared/Task';
import { PreloadCache } from '~/utils/preloadCache';

interface PreloadedProduct {
  data: ProductAPI;
  lang: string;
}

export class ProductState {
  private readonly id: string | number;
  /** The `?sku=` the page opened with; part of the SSR preload key. */
  private readonly initialSku?: string;
  public readonly reviews: ReviewsStore;

  private product: Nullable<Product> = null;
  private offer: Nullable<Offer> = null;
  /** SKU the user picked last and is still waiting for. */
  private pendingSku: Nullable<string> = null;
  /** Language the product was loaded in; offers loaded on demand must match it. */
  private dataLang?: string;

  private static preloadedData = new PreloadCache<PreloadedProduct>({
    max: 500,
    ttlMs: 60_000,
  });

  constructor(id: string | number, sku?: string) {
    this.id = id;
    this.initialSku = sku;
    this.reviews = new ReviewsStore(id);

    this.init();
    makeAutoObservable(this);
  }

  private static preloadKey(productId: string | number, sku?: string) {
    return sku ? `${productId}?sku=${sku}` : String(productId);
  }

  public static async preload(params: LoadProductParams, serverLang?: string) {
    const lang = getRequestLang(serverLang);
    const { data } = await getProductById(params, lang);

    ProductState.preloadedData.set(
      ProductState.preloadKey(params.productId, params.sku),
      { data, lang },
    );

    let reviews: Awaited<ReturnType<typeof ReviewsStore.preload>> | undefined;

    if (company.useReviewsOnProductPage) {
      try {
        reviews = await ReviewsStore.preload(data.id);
      } catch {
        // Reviews will be fetched client-side as fallback
      }
    }

    return {
      product: data,
      reviews,
    };
  }

  public static removePreloadedData(productId: string | number) {
    this.preloadedData.delete(String(productId));
  }

  public get data() {
    return this.product;
  }

  public get selectedOffer() {
    return this.offer;
  }

  public get notFound() {
    if (typeof window === 'undefined') {
      return !this.product;
    }

    return (
      isAxiosError(this.request.lastError) &&
      this.request.lastError.response?.status === 404 &&
      !this.request.isLoading
    );
  }

  public readonly request = new Task(
    async ({
      preferredSku,
      ...params
    }: LoadProductParams & { preferredSku?: string }) => {
      const lang = getRequestLang();
      const { data } = await getProductById(
        {
          ...params,
          sku: preferredSku ?? params.sku,
        },
        lang,
      );

      this.applyProduct(data, preferredSku, lang);
    },
  );

  /**
   * Loads an offer the compact response left out, then selects it — unless
   * another SKU was requested or another product was loaded in the meantime.
   */
  public readonly offerRequest = new Task(async (sku: string) => {
    const product = this.product;

    if (!product) {
      throw new Error('Product not found');
    }

    try {
      await Product.loadFullOffer(product, sku, this.dataLang);
    } catch (error) {
      this.completeOfferLoad(sku, false);

      throw error;
    }

    if (this.product === product) {
      this.completeOfferLoad(sku, true);
    }
  });

  /** True until the offer picked last has arrived or failed, whatever other loads finish meanwhile. */
  public get isOfferLoading() {
    return this.pendingSku !== null;
  }

  /**
   * Selects a loaded offer synchronously; an offer known only from
   * `variants[]` is loaded first and selected once it arrives.
   */
  public selectOffer(code: string): Nullable<Offer> {
    const product = this.product;

    if (!product) {
      throw new Error('Product not found');
    }

    const offer = product.offers.find(({ sku }) => sku === code);

    if (!offer) {
      this.loadOffer(code);

      return null;
    }

    this.pendingSku = null;
    this.offer = offer;

    return this.offer;
  }

  private loadOffer(sku: string) {
    if (this.pendingSku === sku) {
      return;
    }

    this.pendingSku = sku;
    this.offerRequest.run(sku).catch(() => undefined);
  }

  private completeOfferLoad(sku: string, loaded: boolean) {
    if (this.pendingSku !== sku) {
      return;
    }

    this.pendingSku = null;

    if (loaded) {
      this.selectOffer(sku);
    }
  }

  private init() {
    if (typeof window === 'undefined') {
      const preloaded = ProductState.preloadedData.get(
        ProductState.preloadKey(this.id, this.initialSku),
      );

      if (preloaded && Product.compareId(this.id, preloaded.data)) {
        this.applyProduct(preloaded.data, undefined, preloaded.lang);
        bottomNavigationStore.setProductState(this);
      }

      return;
    }

    const product = window.__INITIAL_STATE__?.product;

    if (product && Product.compareId(this.id, product)) {
      this.applyProduct(
        product,
        undefined,
        window.__INITIAL_STATE__?.context?.serverLang || company.config.lang,
      );
      bottomNavigationStore.setProductState(this);
    }
  }

  /**
   * The offer is chosen here, before the first render, so the page never
   * shows (or reports) a default offer that a link or query already overrode.
   */
  private applyProduct(data: ProductAPI, preferredSku?: string, lang?: string) {
    this.product = new Product(data);
    this.dataLang = lang;
    this.pendingSku = null;

    // Default to the first offer that is in stock: starting from a sold-out
    // offer renders a "Sold out" button even though sibling offers (other
    // colors/sizes) are available. LMS counts stock across warehouses — its ETA
    // warehouse is virtual and unknown on the server, so keying by it made SSR
    // and the client pick different offers.
    const warehouse = orderStore.etaWarehouseCode;
    const offer =
      this.product.offers.find(({ sku }) => sku === preferredSku) ||
      (company.hasNewLogistics
        ? this.product.defaultOffer
        : this.product.offers.find(
            ({ sellableWarehouses }) =>
              Number(sellableWarehouses?.[warehouse]) > 0,
          )) ||
      this.product.offers[0];

    this.reviews.setId(data.id);

    if (offer) {
      this.selectOffer(offer.sku);
    }
  }
}
